/**
 * DimensionLabelGroup - Reusable component for rendering taxonomy dimension labels
 *
 * Atomic level: Molecule
 * Composes: TaxonomyTag
 *
 * Handles:
 * - Rendering grouped labels with category headers
 * - Empty state messaging
 * - Consistent styling across all dimensions
 *
 * Following GO UI Design System patterns
 */

import React from 'react';
import { cn } from '../../../../lib/utils';
import { TaxonomyTag, type TaxonomyTagVariant } from './TaxonomyTag';
import type { TaxonomyLabel } from '../../../../types/record';
import { formatConductLabel, formatTaxonomyLabel } from './taxonomyLabelFormatting';

/**
 * A group of labels with a category name
 */
export interface LabelGroup {
  /** Category/group name to display (e.g., "Implicit", "Material Distress", "Customer service") */
  name: string;
  /** Labels in this group */
  labels: TaxonomyLabel[];
}

interface DimensionLabelGroupProps {
  /** Dimension title (e.g., "Dissatisfaction", "Root Causes") */
  title: string;
  /** Label groups to render - can be explicit categories or auto-grouped by categoryName */
  groups: LabelGroup[];
  /** TaxonomyTag variant for color styling */
  variant: TaxonomyTagVariant;
  /** Message when no labels exist */
  emptyMessage?: string;
  /** Optional className */
  className?: string;
}

/**
 * DimensionLabelGroup Component
 *
 * Renders a taxonomy dimension with its labels grouped by category.
 * Used throughout RecordDetail to display conduct labels consistently.
 */
const DimensionLabelGroup: React.FC<DimensionLabelGroupProps> = ({
  title,
  groups,
  variant,
  emptyMessage = 'No labels detected',
  className,
}) => {
  const hasLabels = groups.some((g) => g.labels.length > 0);

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <h5 className="text-base font-medium text-foreground">{title}</h5>

      {hasLabels ? (
        <div className="flex flex-col gap-4">
          {groups.map(
            (group) =>
              group.labels.length > 0 && (
                <div key={group.name} className="flex flex-col gap-2">
                  <p className="text-xs text-muted-foreground">{group.name}</p>
                  <div className="flex flex-wrap gap-2">
                    {group.labels.map((label) => {
                      const displayLabel = formatConductLabel(label.name || label.id, variant, label.id);

                      return (
                        <TaxonomyTag
                          key={label.id}
                          label={label.id}
                          displayLabel={displayLabel}
                          variant={variant}
                          tooltip={{
                            label_name: displayLabel,
                            rationale: label.rationale,
                            confidence: label.confidence,
                            regulation_reference: label.regulation_reference,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              )
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      )}
    </div>
  );
};

/**
 * Helper to group flat labels by categoryName
 *
 * Takes a flat array of TaxonomyLabels (with category/categoryName fields)
 * and groups them into LabelGroup[] for use with DimensionLabelGroup.
 *
 * @param labels - Flat array of taxonomy labels with optional category/categoryName
 * @returns Array of LabelGroups sorted by category name
 */
export const groupLabelsByCategory = (labels: TaxonomyLabel[]): LabelGroup[] => {
  if (!labels || labels.length === 0) return [];

  const grouped = labels.reduce<Record<string, TaxonomyLabel[]>>((acc, label) => {
    // Use categoryName if available, fall back to category, then 'Other'
    const key = (label as TaxonomyLabel & { categoryName?: string; category?: string }).categoryName
      || (label as TaxonomyLabel & { category?: string }).category
      || 'Other';
    (acc[key] = acc[key] || []).push(label);
    return acc;
  }, {});

  return Object.entries(grouped)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, labels]) => ({
      name: formatTaxonomyLabel(name),
      labels,
    }));
};

export { DimensionLabelGroup };
export default DimensionLabelGroup;
